import React from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/nova/components';

import { formatLabel } from './BannerRail';
import { PreviewFrame, fitScale, formatFrameSize } from './PreviewFrame';
import * as S from './PlayableComposer.styles';

/** One size's preview document ('' while it is being built; null when there is none to show: this environment
 *  cannot build it, or `note` says why). */
export interface BannerSizeDoc {
  format: string;
  html: string | null;
  /** Translated. Why this size shows no preview (default: previews are not available here). */
  note?: string;
}

export interface BannerSizesStageProps {
  sizes: BannerSizeDoc[];
  /** Translated accessible name of each frame, `{{size}}` interpolated by the caller's key. */
  titleOf: (format: string) => string;
}

/** A size card's narrowest width: the Image Banners size card's, so the two grids read as one. Layout. */
const CARD_MIN_W = 212;
/** The gap between cards (SPACE[16]), which a two-cell card spans too. Layout. */
const CARD_GAP = 16;
/** The height of the stage every size plays on: taller than an image size's, since a live banner is read, not
 *  glanced at (a half page stays legible). Layout. */
const STAGE_H = 220;
/** The stage's padding on each side (SPACE[8]) plus the card's 1px border. Layout. */
const STAGE_INSET = 8 + 1;
/** A size this wide (or wider) spans two cells, so a leaderboard or a mobile strip stays readable. */
const WIDE_RATIO = 3;

/** Whether a size is a wide strip, which spans two cells. Pure. */
const isWide = (format: string): boolean => {
  const { w, h } = formatFrameSize(format);
  return w / h >= WIDE_RATIO;
};

/** The narrowest the size grid can be without clipping a card: two cells and their gap once any size is wide (its
 *  card spans two, so a narrower grid pushes it past the edge), else one cell. The composer goes tabbed below it. Pure. */
export const bannerSizesMinWidth = (formats: readonly string[]): number =>
  formats.some(isWide) ? 2 * CARD_MIN_W + CARD_GAP : CARD_MIN_W;

/** The box a size is shrunk into: its card's narrowest stage (one cell, or two with the gap), so a frame never
 *  overflows its card however narrow the grid gets. Pure. */
const stageBox = (wide: boolean): { w: number; h: number } => ({
  w: (wide ? 2 * CARD_MIN_W + CARD_GAP : CARD_MIN_W) - 2 * STAGE_INSET,
  h: STAGE_H - 2 * STAGE_INSET,
});

/**
 * Every size of an animated banner as a card, like the Image Banners size grid: each plays live in its own sandboxed
 * frame at its real pixels (scaled onto the card's stage), with its size in the card's footer; a wide strip spans
 * two cells. Replay restarts them all together, so the sizes can be compared loop by loop; a replay or an edit
 * cross-fades each size to its fresh document, so no size ever blanks.
 */
export const BannerSizesStage = ({ sizes, titleOf }: BannerSizesStageProps): React.ReactElement => {
  const { t } = useTranslation();
  const [run, setRun] = React.useState(0);
  const any = sizes.some((s) => Boolean(s.html));
  return (
    <S.SizesWrap>
      {any ? (
        <S.SizesHead>
          <Button variant="text" icon="refresh" label={t('Replay')} onClick={() => setRun((n) => n + 1)} />
        </S.SizesHead>
      ) : null}
      <S.SizeCardGrid $minW={CARD_MIN_W}>
        {sizes.map(({ format, html, note }) => {
          const { w, h } = formatFrameSize(format);
          const wide = isWide(format);
          const box = stageBox(wide);
          const scale = fitScale(w, h, box.w, box.h);
          return (
            <S.SizeCard key={format} $wide={wide}>
              <S.SizeStage $h={STAGE_H}>
                {html ? (
                  <PreviewFrame run={run} html={html} w={w} h={h} scale={scale} title={titleOf(format)} />
                ) : (
                  <S.SizePlaceholder $w={w * scale} $h={h * scale}>
                    {html === null && note ? (
                      <S.NoteText raw text={note} />
                    ) : html === null ? (
                      <S.NoteText text="Live previews are not available in this environment yet." />
                    ) : (
                      <S.Spinner aria-hidden="true" />
                    )}
                  </S.SizePlaceholder>
                )}
              </S.SizeStage>
              <S.SizeCardFoot>
                <S.SizeCardLabel raw text={formatLabel(format)} />
              </S.SizeCardFoot>
            </S.SizeCard>
          );
        })}
      </S.SizeCardGrid>
    </S.SizesWrap>
  );
};

export default BannerSizesStage;
